import { useState } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { CtaActionEditor } from "./CtaActionEditor";
import { SortableList, DragHandleIcon } from "./SortableList";
import { Navigation, Palette, Type, ChevronDown, Eye, EyeOff, GripVertical } from "lucide-react";

const VARIANT_OPTIONS = [
  { value: "default",     label: "Standard (Template-Default)" },
  { value: "transparent", label: "Transparent (über Hero)" },
  { value: "solid",       label: "Solid (ohne Blur)" },
  { value: "blur",        label: "Glass / Blur" },
  { value: "dark",        label: "Dark Mode" },
];

const SIZE_OPTIONS = [
  { value: "sm", label: "Klein" },
  { value: "md", label: "Normal (Standard)" },
  { value: "lg", label: "Groß" },
];

/**
 * Zentrale Komponente zum Editieren der Nav-Konfiguration.
 *
 * Props:
 *   value: nav-Objekt aus editContent.nav (wird normalisiert vom Parent)
 *   onChange: (patch) => void — Parent wendet das Patch auf editContent.nav an
 *   customPages: Array aus editContent.custom_pages für Link-Reordering
 *   companyPhone/Email: Fallbacks für CTA-Aktion
 */
export function NavEditor({ value, onChange, customPages = [], companyPhone = "", companyEmail = "" }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });

  // Link-Reihenfolge: wir arbeiten mit slugs, Anzeige mit titles
  const navPages = (customPages || []).filter(p => p?.in_nav && p?.slug && p?.title);
  const currentOrder = Array.isArray(v.link_order) && v.link_order.length > 0
    ? [...v.link_order.filter(s => navPages.some(p => p.slug === s)),
       ...navPages.filter(p => !v.link_order.includes(p.slug)).map(p => p.slug)]
    : navPages.map(p => p.slug);

  return (
    <div className="space-y-3" data-testid="nav-editor">
      {/* Variant */}
      <div>
        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <Palette size={10} /> Nav-Stil
        </Label>
        <Select value={v.variant || "default"} onValueChange={(val) => set({ variant: val })}>
          <SelectTrigger className="rounded-md mt-1 text-sm h-9" data-testid="nav-variant">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VARIANT_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Hintergrund-Farbe</Label>
          <div className="flex items-center gap-1.5 mt-1">
            <input
              type="color"
              value={normalizeColor(v.bg_color)}
              onChange={e => set({ bg_color: e.target.value })}
              className="h-9 w-9 rounded-sm border border-border cursor-pointer"
              data-testid="nav-bg-color-picker"
            />
            <Input
              value={v.bg_color || ""}
              onChange={e => set({ bg_color: e.target.value })}
              placeholder="z.B. #1a0033"
              className="rounded-md text-xs font-mono h-9"
              data-testid="nav-bg-color"
            />
            {v.bg_color && (
              <button type="button" onClick={() => set({ bg_color: "" })} className="text-[10px] text-muted-foreground hover:text-destructive px-1" data-testid="nav-bg-color-clear">×</button>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Text-Farbe</Label>
          <div className="flex items-center gap-1.5 mt-1">
            <input
              type="color"
              value={normalizeColor(v.text_color)}
              onChange={e => set({ text_color: e.target.value })}
              className="h-9 w-9 rounded-sm border border-border cursor-pointer"
              data-testid="nav-text-color-picker"
            />
            <Input
              value={v.text_color || ""}
              onChange={e => set({ text_color: e.target.value })}
              placeholder="z.B. #ffffff"
              className="rounded-md text-xs font-mono h-9"
              data-testid="nav-text-color"
            />
            {v.text_color && (
              <button type="button" onClick={() => set({ text_color: "" })} className="text-[10px] text-muted-foreground hover:text-destructive px-1" data-testid="nav-text-color-clear">×</button>
            )}
          </div>
        </div>
      </div>

      {/* Logo */}
      <details className="bg-muted/30 rounded-md border border-border/60 group/lg">
        <summary className="flex items-center gap-2 p-2 cursor-pointer list-none text-[11px] font-semibold uppercase tracking-widest text-foreground">
          <Type size={11} /> Logo
          <ChevronDown size={12} className="ml-auto opacity-60 group-open/lg:rotate-180 transition-transform" />
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Logo-Text (überschreibt Firmenname)</Label>
            <Input
              value={v.logo_text || ""}
              onChange={e => set({ logo_text: e.target.value })}
              placeholder="Leer = Firmenname verwenden"
              className="rounded-md text-sm mt-1 h-9"
              data-testid="nav-logo-text"
            />
            <p className="text-[10px] text-muted-foreground italic mt-1">Wenn gesetzt, wird statt des Firmennamens (oder Logo-Bilds) dieser Text als Wortmarke angezeigt.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Logo-Größe</Label>
            <Select value={v.logo_size || "md"} onValueChange={(val) => set({ logo_size: val })}>
              <SelectTrigger className="rounded-md mt-1 text-sm h-9" data-testid="nav-logo-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SIZE_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </details>

      {/* Nav-CTA */}
      <details className="bg-muted/30 rounded-md border border-border/60 group/na" open>
        <summary className="flex items-center gap-2 p-2 cursor-pointer list-none text-[11px] font-semibold uppercase tracking-widest text-foreground">
          <Navigation size={11} /> Nav-CTA (oben rechts)
          <ChevronDown size={12} className="ml-auto opacity-60 group-open/na:rotate-180 transition-transform" />
        </summary>
        <div className="p-2 pt-0 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sichtbar</Label>
            <Switch
              checked={v.cta_visible !== false}
              onCheckedChange={(on) => set({ cta_visible: on })}
              data-testid="nav-cta-visible"
            />
          </div>
          {v.cta_visible !== false && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Button-Text</Label>
                <Input
                  value={v.cta_text || ""}
                  onChange={e => set({ cta_text: e.target.value })}
                  placeholder="z.B. Jetzt kontaktieren (leer = Template-Default)"
                  className="rounded-md text-sm mt-1 h-9"
                  data-testid="nav-cta-text"
                />
              </div>
              <CtaActionEditor
                value={v.cta_action}
                onChange={(next) => set({ cta_action: next })}
                customPages={customPages}
                companyPhone={companyPhone}
                companyEmail={companyEmail}
                testidPrefix="nav-cta-action"
                compact
              />
            </>
          )}
        </div>
      </details>

      {/* Link-Reihenfolge */}
      {navPages.length > 1 && (
        <details className="bg-muted/30 rounded-md border border-border/60 group/lo">
          <summary className="flex items-center gap-2 p-2 cursor-pointer list-none text-[11px] font-semibold uppercase tracking-widest text-foreground">
            <GripVertical size={11} /> Link-Reihenfolge
            <ChevronDown size={12} className="ml-auto opacity-60 group-open/lo:rotate-180 transition-transform" />
          </summary>
          <div className="p-2 pt-0 space-y-1">
            <p className="text-[10px] text-muted-foreground italic mb-1.5">Ziehe die Einträge in die gewünschte Reihenfolge — sie erscheinen in genau dieser Folge in der Nav.</p>
            <SortableList
              items={currentOrder}
              onReorder={(newOrder) => set({ link_order: newOrder })}
              itemKey="nav-link-order"
              renderItem={(slug, { dragHandleProps }) => {
                const p = navPages.find(x => x.slug === slug);
                if (!p) return null;
                return (
                  <div className="flex items-center gap-2 p-1.5 bg-background rounded-sm border border-border" data-testid={`nav-link-item-${slug}`}>
                    <button {...dragHandleProps}><DragHandleIcon /></button>
                    <span className="text-[11px] flex-1 truncate">{p.title}</span>
                    <span className="text-[9px] text-muted-foreground font-mono">{p.slug}</span>
                  </div>
                );
              }}
            />
          </div>
        </details>
      )}
    </div>
  );
}

function normalizeColor(c) {
  if (!c) return "#ffffff";
  if (c.startsWith("#") && (c.length === 7 || c.length === 4)) return c;
  return "#ffffff";
}
